import { useCallback, useState } from 'react'

import pdfMake from 'pdfmake/build/pdfmake'
import vfsFonts from 'pdfmake/build/vfs_fonts'
import { TDocumentDefinitions } from 'pdfmake/interfaces'
import {
    DimensionExportToReport,
    OverviewPreviewReportStructureExport,
    PurposesExportToReport,
    SummaryExportToReport,
} from '../structure/structureOverviewPreviewReport'
import { thaiWordWrap } from '../../../utils/wordcut/thaiWordWrap'
import { configPdfMakeDefault } from '../../../utils/pdfMake'
import { ModeTableRecommended, rewriteRecommendToReport } from '../components/OverviewTableRecommend'
import { FieldType } from '../../questionnaire/FormStructureQuestionnaireType'

const { vfs } = vfsFonts.pdfMake
pdfMake.vfs = vfs
pdfMake.fonts = {
    THSarabun: {
        normal: 'THSarabunNew.ttf',
        bold: 'THSarabunNew-Bold.ttf',
        italics: 'THSarabunNew-Italic.ttf',
        bolditalics: 'THSarabunNew-BoldItalic.ttf',
    },
    Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Medium.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-MediumItalic.ttf',
    },
}
// type ReturnTypeHookUseExportReportOverviewPDF = ReturnType<typeof useExportReportOverviewPDF>
type UseExportReportOverviewPDF = {}
const useExportReportOverviewPDF = (props: UseExportReportOverviewPDF) => {
    const [isLoadingDownloadPdf, setLoadingDownloadPdf] = useState(false)
    const [isLoadingOpenPdf, setLoadingOpenPdf] = useState(false)
    const getDocDefinitionPdf = useCallback(({ data = [] }: { data: OverviewPreviewReportStructureExport[] }) => {
        const docDefinition = {
            ...configPdfMakeDefault,
            content: [
                ...data.map(({ dimensions, ...item }) => {
                    return [
                        ...generateQuestionnaire({
                            name: item.questionnaireName,
                            image: item.questionnaireRef as string,
                            data: dimensions,
                        }),
                        ...generateDimension({ data: dimensions }),
                    ]
                }),
            ],
        } as TDocumentDefinitions
        return docDefinition
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const generateTableRecommend = useCallback(
        ({
            titleCell,
            data = [],
            prefixLabel,
            mode = 'recommend',
        }: {
            data: PurposesExportToReport[]
            titleCell: string
            prefixLabel: string
            mode?: ModeTableRecommended
        }) => {
            const newDataSet = rewriteRecommendToReport({
                //@ts-ignore
                dataSource: data,
                prefixLabel,
                mode,
            }) as PurposesExportToReport[]

            const bodyEmpty = [
                [
                    {
                        text: thaiWordWrap(titleCell, 69, '\n'), // Header column ด้านซ้ายของตาราง กรณี ไม่มีข้อมูล
                        // text: '',
                        style: 'tableHeader',
                        alignment: 'left',
                        fillColor: '#d9d9d9',
                        noWrap: false,
                    },
                    { text: 'คำแนะนำโดยละเอียด', style: 'tableHeader', alignment: 'center', fillColor: '#d9d9d9' },
                ],
                [
                    { text: '\n ไม่มีข้อมูล \n\n\n', colSpan: 2, alignment: 'center', style: 'text' },
                    { text: '\n ไม่มีข้อมูล \n\n\n', colSpan: 2, alignment: 'center', style: 'text' },
                ],
            ]

            return {
                style: 'tableExample',
                table: {
                    widths: ['40%', '60%'],
                    headerRows: 1,
                    // dontBreakRows: true,
                    // keepWithHeaderRows: 1,
                    body: newDataSet.length
                        ? [
                              [
                                  {
                                      text: thaiWordWrap(titleCell, 69, '\n'), // Header column ด้านซ้ายของตาราง
                                      // text: '',
                                      style: 'tableHeader',
                                      alignment: 'left',
                                      fillColor: '#d9d9d9',
                                      noWrap: false,
                                  },
                                  {
                                      text: 'คำแนะนำโดยละเอียด',
                                      style: 'tableHeader',
                                      alignment: 'center',
                                      fillColor: '#d9d9d9',
                                  },
                              ],
                              ...newDataSet.map((item, index: number) => {
                                  return [
                                      {
                                          text: thaiWordWrap(item.name, 72, '\n'), // Content column ด้านซ้ายของตาราง คำแนะนำ
                                          // text: '',
                                          alignment: 'left',
                                          style: 'text',
                                          bold: true,
                                          noWrap: false,
                                      },
                                      {
                                          stack: [
                                              ...item.questions.map(
                                                  ({ autoRecommended = [], fieldType, ...question }, index: number) => {
                                                      return [
                                                          {
                                                              text: thaiWordWrap(
                                                                  `${question.name.replace(/  +/g, ' ')}`,
                                                                  107,
                                                                  '\n',
                                                              ), // Content column ด้านขวาของตาราง คำแนะนำ ในส่วน หัวข้อการประเมิน
                                                              // text: '',
                                                              bold: true,
                                                              margin: [10, 5, 5, 5],
                                                              noWrap: false,
                                                          },

                                                          (() => {
                                                              if (mode === 'recommend') {
                                                                  return {
                                                                      text: thaiWordWrap(
                                                                          `- ${question.value}` || '-',
                                                                          103,
                                                                          '\n',
                                                                      ), // Content column ด้านขวา ในส่วนที่เป็น คำแนะนำ เฉพาะ ตาราง หมายเหตุและข้อคิดเห็นเจ้าหน้าที่
                                                                      // text: '',
                                                                      margin: [20, 0, 0, 0],
                                                                      style: 'text',
                                                                      bold: true,
                                                                      noWrap: false,
                                                                  }
                                                              }

                                                              return autoRecommended.map((autoRec) => {
                                                                  if (fieldType === FieldType.CHECKBOX_GROUP) {
                                                                      return {
                                                                          stack: [
                                                                              {
                                                                                  text: thaiWordWrap(
                                                                                      `หัวข้อใหญ่ที่ ${autoRec.subQuestionID}. ${autoRec.subQuestionName}` ||
                                                                                          '-',
                                                                                      100,
                                                                                      '\n',
                                                                                  ), // Content column ด้านขวา ในส่วนที่เป็น ตัวเลือก เฉพาะ ตาราง คำแนะนำ
                                                                                  // text: '',
                                                                                  bold: true,
                                                                                  margin: [20, 5, 5, 0],
                                                                                  noWrap: false,
                                                                              },
                                                                              autoRec.subAnswer?.map((subAnswer) => {
                                                                                  return {
                                                                                      stack: [
                                                                                          {
                                                                                              text: thaiWordWrap(
                                                                                                  `ตัวเลือกที่ ${subAnswer.answerOrder}. ${subAnswer.answerName}` ||
                                                                                                      '-',
                                                                                                  100,
                                                                                                  '\n',
                                                                                              ), // Content column ด้านขวา ในส่วนที่เป็น ตัวเลือก เฉพาะ ตาราง คำแนะนำ
                                                                                              // text: '',
                                                                                              bold: true,
                                                                                              margin: [30, 0, 0, 0],
                                                                                              noWrap: false,
                                                                                          },
                                                                                          {
                                                                                              text: thaiWordWrap(
                                                                                                  `- ${subAnswer.answerSuggestion}` ||
                                                                                                      '-',
                                                                                                  106,
                                                                                                  '\n',
                                                                                              ), // Content column ด้านขวา ในส่วนที่เป็น คำแนะนำ เฉพาะ ตาราง คำแนะนำ
                                                                                              // text: '',
                                                                                              margin: [30, 0, 0, 0],
                                                                                              noWrap: false,
                                                                                          },
                                                                                      ],
                                                                                  }
                                                                              }),
                                                                          ],
                                                                      }
                                                                  } else {
                                                                      return {
                                                                          stack: [
                                                                              {
                                                                                  text: thaiWordWrap(
                                                                                      `ตัวเลือกที่ ${autoRec.answerOrder}. ${autoRec.answerName}` ||
                                                                                          '-',
                                                                                      100,
                                                                                      '\n',
                                                                                  ), // Content column ด้านขวา ในส่วนที่เป็น ตัวเลือก เฉพาะ ตาราง คำแนะนำ
                                                                                  // text: '',
                                                                                  bold: true,
                                                                                  margin: [20, 0, 0, 0],
                                                                                  noWrap: false,
                                                                              },
                                                                              {
                                                                                  text: thaiWordWrap(
                                                                                      `- ${autoRec.answerSuggestion}` ||
                                                                                          '-',
                                                                                      106,
                                                                                      '\n',
                                                                                  ), // Content column ด้านขวา ในส่วนที่เป็น คำแนะนำ เฉพาะ ตาราง คำแนะนำ
                                                                                  // text: '',
                                                                                  margin: [25, 0, 0, 0],
                                                                                  noWrap: false,
                                                                              },
                                                                          ],
                                                                      }
                                                                  }
                                                              })
                                                          })(),
                                                      ]
                                                  },
                                              ),
                                          ],
                                      },
                                  ]
                              }),
                          ]
                        : bodyEmpty,
                },
            }
        },
        [],
    )

    const generateTableReport = useCallback(
        ({
            data = [],
            titleCell,
            prefixLabel,
        }: {
            data: SummaryExportToReport[]
            titleCell: string
            prefixLabel: string
        }) => {
            const isScoreAdminUndefined = data.every((dimension) => typeof dimension.scoreAdmin === 'undefined')
            const isScoreUserUndefined = data.every((dimension) => typeof dimension.scoreUser === 'undefined')

            const isScoreAdminNull = data.every((item) => item.scoreAdmin === null)
            const isScoreUserNull = data.every((item) => item.scoreUser === null)

            let totalScoreFull = 0
            let isNumberScoreFull = true
            const arrayTotalScoreFull = data.map((item) => item.scoreFull).filter((item) => typeof item === 'number')
            if (arrayTotalScoreFull.length > 0) {
                totalScoreFull = arrayTotalScoreFull.reduce((acc, crr) => {
                    return (acc as number) + (crr as number)
                })
            } else {
                isNumberScoreFull = false
            }

            let totalScoreUser: number | null | undefined = 0
            let totalScoreAdmin: number | null | undefined = 0

            const arrayScoreUser = data.map((item) => item.scoreUser).filter((item) => typeof item === 'number')
            if (arrayScoreUser.length > 0) {
                totalScoreUser = arrayScoreUser.reduce((acc, crr) => {
                    return (acc as number) + (crr as number)
                })
            }

            const arrayScoreAdmin = data.map((item) => item.scoreAdmin).filter((item) => typeof item === 'number')
            if (arrayScoreAdmin.length > 0) {
                totalScoreAdmin = arrayScoreAdmin.reduce((acc, crr) => {
                    return (acc as number) + (crr as number)
                })
            }

            let percentScoreUser
            let percentScoreAdmin

            if (typeof totalScoreUser === 'number') {
                percentScoreUser = (totalScoreUser * 100) / totalScoreFull
            }

            if (typeof totalScoreAdmin === 'number') {
                percentScoreAdmin = (totalScoreAdmin * 100) / totalScoreFull
            }

            return {
                style: 'tableExample',
                table: {
                    // widths: ['*', 70, 100, 100, 100, 100],
                    widths: ['34%', '10%', '12%', '16%', '12%', '16%'],
                    // widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
                    headerRows: 2,
                    dontBreakRows: true,
                    keepWithHeaderRows: 2,
                    body: [
                        [
                            {
                                text: titleCell,
                                style: 'tableHeader',
                                alignment: 'center',
                                rowSpan: 2,
                                fillColor: '#d9d9d9',
                                fontSize: 14,
                            },
                            {
                                text: 'คะแนนเต็ม',
                                style: 'tableHeader',
                                alignment: 'center',
                                rowSpan: 2,
                                fillColor: '#d9d9d9',
                                fontSize: 14,
                            },
                            {
                                text: 'คะแนนโดยชุมชน',
                                style: 'tableHeader',
                                alignment: 'center',
                                colSpan: 2,
                                fillColor: '#fced98',
                                fontSize: 14,
                            },
                            {},
                            {
                                text: 'คะแนนเจ้าหน้าที่',
                                style: 'tableHeader',
                                alignment: 'center',
                                colSpan: 2,
                                fillColor: '#d4e8dc',
                                fontSize: 14,
                            },
                            {},
                        ],
                        [
                            {},
                            {},
                            {
                                text: 'คะแนนที่ได้',
                                style: 'tableHeader',
                                alignment: 'center',
                                fillColor: '#fced98',
                                fontSize: 14,
                            },
                            {
                                text: 'ระดับคะแนน',
                                style: 'tableHeader',
                                alignment: 'center',
                                fillColor: '#fced98',
                                fontSize: 14,
                            },
                            {
                                text: 'คะแนนที่ได้',
                                style: 'tableHeader',
                                alignment: 'center',
                                fillColor: '#d4e8dc',
                                fontSize: 14,
                            },
                            {
                                text: 'ระดับคะแนน',
                                style: 'tableHeader',
                                alignment: 'center',
                                fillColor: '#d4e8dc',
                                fontSize: 14,
                            },
                        ],
                        ...data.map((dimension, index: number) => {
                            return [
                                {
                                    stack: [
                                        {
                                            text: `${prefixLabel}${index + 1} ${dimension.name}`,
                                            alignment: 'left',
                                            style: 'text',
                                            fontSize: 14,
                                            margin: [0, 0, 0, 0],
                                            noWrap: false,
                                        },
                                    ],
                                },
                                {
                                    text: dimension.scoreFull,
                                    alignment: 'center',
                                    style: 'text',
                                    fontSize: 14,
                                    margin: [0, 0, 0, 0],
                                },
                                {
                                    text:
                                        typeof dimension.scoreUser === 'number' ? dimension.scoreUser.toFixed(2) : '-',
                                    alignment: 'center',
                                    fillColor: '#fffce9',
                                    style: 'text',
                                    fontSize: 14,
                                    margin: [0, 0, 0, 0],
                                },
                                {
                                    text: dimension.descriptionUser ?? '-',
                                    alignment: 'center',
                                    fillColor: '#fffce9',
                                    style: 'text',
                                    fontSize: 14,
                                    margin: [0, 0, 0, 0],
                                },
                                {
                                    text:
                                        typeof dimension.scoreAdmin === 'number'
                                            ? dimension.scoreAdmin.toFixed(2)
                                            : '-',
                                    alignment: 'center',
                                    fillColor: '#f6fbf7',
                                    style: 'text',
                                    fontSize: 14,
                                    margin: [0, 0, 0, 0],
                                },
                                {
                                    text: dimension.descriptionAdmin ?? '-',
                                    alignment: 'center',
                                    fillColor: '#f6fbf7',
                                    style: 'text',
                                    fontSize: 14,
                                    margin: [0, 0, 0, 0],
                                },
                            ]
                        }),
                        [
                            {
                                text: 'รวม',
                                alignment: 'center',
                                style: 'tableHeader',
                                fontSize: 14,
                                margin: [0, 0, 0, 0],
                            }, // รวม
                            {
                                text:
                                    isNumberScoreFull && (!isScoreAdminUndefined || !isScoreUserUndefined)
                                        ? totalScoreFull
                                        : '-',
                                alignment: 'center',
                                style: 'tableHeader',
                                fontSize: 14,
                                margin: [0, 0, 0, 0],
                            }, // รวมคะแนนเต็ม
                            {
                                text: !isScoreUserUndefined && !isScoreUserNull ? totalScoreUser?.toFixed(2) : '-',
                                alignment: 'center',
                                fillColor: '#fffce9',
                                style: 'tableHeader',
                                fontSize: 14,
                                margin: [0, 0, 0, 0],
                            }, // คะแนนที่ได้ user [ชุมชน]
                            {
                                text:
                                    typeof percentScoreUser === 'number' && !isScoreUserUndefined && !isScoreUserNull
                                        ? `คิดเป็น ${percentScoreUser.toFixed(2)}%`
                                        : '-',
                                alignment: 'center',
                                fillColor: '#fffce9',
                                style: 'tableHeader',
                                fontSize: 14,
                                margin: [0, 0, 0, 0],
                            }, // ระดับคะแนน admin [ชุมชน]
                            {
                                text: !isScoreAdminUndefined && !isScoreAdminNull ? totalScoreAdmin?.toFixed(2) : '-',
                                alignment: 'center',
                                fillColor: '#f6fbf7',
                                style: 'tableHeader',
                                fontSize: 14,
                                margin: [0, 0, 0, 0],
                            }, // คะแนนที่ได้   [เจ้าหน้าที่]
                            {
                                text:
                                    typeof percentScoreAdmin === 'number' && !isScoreAdminUndefined && !isScoreAdminNull
                                        ? `คิดเป็น ${percentScoreAdmin.toFixed(2)}%`
                                        : '-',
                                alignment: 'center',
                                fillColor: '#f6fbf7',
                                style: 'tableHeader',
                                fontSize: 14,
                                margin: [0, 0, 0, 0],
                            }, // ระดับคะแนน
                        ],
                    ],
                },
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const generateQuestionnaire = useCallback(
        ({ name, image, data }: { name: string; image: string; data: DimensionExportToReport[] }) => {
            return [
                {
                    text: `${name}`,
                    style: 'header',
                },
                {
                    stack: [
                        {
                            image: image,
                            width: 400,
                            height: 400,
                            alignment: 'center',
                            pageBreak: 'after',
                        },
                    ],
                    margin: [30, 20, 20, 20],
                    alignment: 'center',
                },
                generateTableReport({ data, titleCell: 'ด้านที่', prefixLabel: 'ด้านที่ ' }),
                {
                    text: '',
                    pageBreak: 'after',
                },
            ]
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const generateDimension = useCallback(({ data }: { data: DimensionExportToReport[] }) => {
        return data.map(({ purposes, ...dimension }, index: number) => {
            const pageBreak = () => {
                if (data.length - 1 === index) {
                    return {
                        text: '',
                    }
                }

                return {
                    text: '',
                    pageBreak: 'after',
                }
            }
            return [
                {
                    text: `ด้านที่ ${index + 1} ${dimension.name}`,
                    style: 'header',
                },
                {
                    stack: [
                        {
                            image: dimension.dimensionRef,
                            width: 450,
                            // height: 550,
                            alignment: 'center',
                            pageBreak: 'after',
                        },
                    ],
                    alignment: 'center',
                    margin: [30, 20, 20, 20],
                },
                generateTableReport({
                    data: purposes as SummaryExportToReport[],
                    titleCell: `เป้าประสงค์`,
                    prefixLabel: `เป้าประสงค์ที่ ${index + 1}.`,
                }),
                {
                    text: '',
                    pageBreak: 'after',
                },
                {
                    text: 'สิ่งที่ควรพัฒนาต่อไป',
                    style: 'header',
                },
                generateTableRecommend({
                    data: purposes,
                    titleCell: `ด้านที่ ${index + 1} ${dimension.name}`,
                    prefixLabel: `เป้าประสงค์ที่ ${index + 1}.`,
                    mode: 'auto-recommend',
                }),
                {
                    text: '',
                    pageBreak: 'after',
                },
                {
                    text: `ข้อเสนอแนะจากเจ้าหน้าที่`,
                    style: 'header',
                },
                generateTableRecommend({
                    data: purposes,
                    titleCell: `ด้านที่ ${index + 1} ${dimension.name}`,
                    prefixLabel: `เป้าประสงค์ที่ ${index + 1}.`,
                    mode: 'recommend',
                }),
                pageBreak(),
            ]
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const downloadPdf = useCallback((props: { data: OverviewPreviewReportStructureExport[] }) => {
        setLoadingDownloadPdf(true)
        const { data = [] } = props
        const docDefinition = getDocDefinitionPdf({ data })
        pdfMake.createPdf(docDefinition).download('รายงานสรุปภาพรวมของแบบประเมิน', () => {
            setLoadingDownloadPdf(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const openPdf = useCallback((props: { data: OverviewPreviewReportStructureExport[] }) => {
        setLoadingOpenPdf(true)
        const { data = [] } = props

        const docDefinition = getDocDefinitionPdf({ data })
        pdfMake.createPdf(docDefinition).open({
            progressCallback: (progress: number) => {
                if (progress === 1) {
                    setLoadingOpenPdf(false)
                }
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return { downloadPdf, openPdf, isLoadingDownloadPdf, isLoadingOpenPdf }
}

export default useExportReportOverviewPDF
